import { useEffect, useState, useMemo, useCallback } from 'react'
import { authService, profileService, supabase } from '@shoots/auth'
import type { User } from '@shoots/auth'
import { posthogService } from '../services/posthogService'

/**
 * Hook to manage authentication state and operations
 */
export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  /**
   * Clear authentication
   */
  const clearAuth = async () => {
    try {
      await authService.logout()
      setUser(null)
      setError(null)

      // Track logout event
      posthogService.capture('logged_out')

      // Reset user identity in PostHog
      posthogService.reset()
    } catch (err) {
      console.error('Logout error:', err)
      setError('Failed to logout')
    }
  }

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = (): boolean => {
    return !!user
  }

  // Computed authentication state for React dependencies
  const authenticated = useMemo(() => {
    return !!user
  }, [user])

  /**
   * Send OTP to phone number
   */
  const sendOTP = async (phone: string) => {
    try {
      setError(null)
      await authService.sendOTP(phone)

      // Track OTP sent event
      posthogService.capture('otp_sent', {
        phone: phone.substring(0, 4) + '****', // Partially masked for privacy
      })
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to send OTP'
      setError(errorMessage)
      throw err
    }
  }

  /**
   * Verify OTP and sign in user
   */
  const verifyOTP = async (phone: string, otp: string) => {
    try {
      setError(null)
      setLoading(true)

      // Call Supabase directly to get both user and session data
      const { data, error } = await supabase.auth.verifyOtp({
        phone: phone,
        token: otp,
        type: 'sms',
      })

      if (error) {
        console.error('OTP verification failed:', error)
        throw error
      }

      if (!data.user || !data.session) {
        throw new Error('Authentication failed')
      }

      // Convert Supabase user to our User type
      const userData: User = {
        id: data.user.id,
        email: data.user.email || '',
        name: data.user.user_metadata?.name || 'User',
        avatar: data.user.user_metadata?.avatar_url,
        createdAt: data.user.created_at || new Date().toISOString(),
        updatedAt: data.user.updated_at || new Date().toISOString(),
      }

      setUser(userData)

      // Fetch profile and identify user in PostHog
      try {
        const profile = await profileService.getCurrentProfile()
        posthogService.identify(profile.auth_id, {
          name: `${profile.first_name} ${profile.last_name}`,
          first_name: profile.first_name,
          last_name: profile.last_name,
          phone: profile.phone_number,
        })
      } catch (err) {
        console.error('Failed to fetch profile for Pulse identification:', err)
        // Fallback to user data if profile fetch fails
        posthogService.identify(userData.id)
      }

      // Track login event
      posthogService.capture('logged_in')

      return userData
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to verify OTP'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  /**
   * Initialize authentication - check for existing session
   */
  const initializeAuth = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const session = await authService.initializeAuth()
      if (session?.user) {
        const userData = await authService.getCurrentUser()
        if (userData) {
          setUser(userData)

          // Fetch profile and identify user in PostHog
          try {
            const profile = await profileService.getCurrentProfile()
            posthogService.identify(profile.auth_id, {
              name: `${profile.first_name} ${profile.last_name}`,
              first_name: profile.first_name,
              last_name: profile.last_name,
              phone: profile.phone_number,
            })
          } catch (err) {
            console.error(
              'Failed to fetch profile for Pulse identification:',
              err
            )
            // Fallback to user data if profile fetch fails
            posthogService.identify(userData.id)
          }
        }
      }
    } catch (err) {
      console.error('Auth initialization error:', err)
      setError('Failed to initialize authentication')
    } finally {
      setLoading(false)
    }
  }, [])

  // Initialize auth state on mount
  useEffect(() => {
    initializeAuth()
  }, [initializeAuth])

  // Listen for Supabase auth state changes
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event)

      if (event === 'SIGNED_IN' && session) {
        // User signed in - update our state
        const userData: User = {
          id: session.user.id,
          email: session.user.email || '',
          name: session.user.user_metadata?.name || 'User',
          avatar: session.user.user_metadata?.avatar_url,
          createdAt: session.user.created_at || new Date().toISOString(),
          updatedAt: session.user.updated_at || new Date().toISOString(),
        }
        setUser(userData)
        setError(null)

        // Fetch profile and identify user in PostHog
        try {
          const profile = await profileService.getCurrentProfile()
          posthogService.identify(profile.auth_id, {
            name: `${profile.first_name} ${profile.last_name}`,
            first_name: profile.first_name,
            last_name: profile.last_name,
            phone: profile.phone_number,
          })
        } catch (err) {
          console.error(
            'Failed to fetch profile for Pulse identification:',
            err
          )
          // Fallback to user data if profile fetch fails
          posthogService.identify(userData.id)
        }
      } else if (event === 'SIGNED_OUT') {
        // User signed out - clear our state
        setUser(null)
        setError(null)

        // Reset user identity in PostHog
        posthogService.reset()
      } else if (event === 'USER_UPDATED' && session) {
        // User data was updated - refresh user info
        const userData: User = {
          id: session.user.id,
          email: session.user.email || '',
          name: session.user.user_metadata?.name || 'User',
          avatar: session.user.user_metadata?.avatar_url,
          createdAt: session.user.created_at || new Date().toISOString(),
          updatedAt: session.user.updated_at || new Date().toISOString(),
        }
        setUser(userData)
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  // Listen for auth errors from API client
  useEffect(() => {
    const handleAuthError = (event: CustomEvent) => {
      console.error('Auth error detected:', event.detail)
      setError('Your session has expired. Please sign in again.')
      setUser(null)
    }

    window.addEventListener('auth:error', handleAuthError as EventListener)

    return () => {
      window.removeEventListener('auth:error', handleAuthError as EventListener)
    }
  }, [])

  return {
    user,
    loading,
    error,
    clearAuth,
    isAuthenticated,
    authenticated,
    sendOTP,
    verifyOTP,
    initializeAuth,
  }
}
